import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MessageItem } from './MessageItem';
import type { ChannelJoinStatus } from '../../contexts/chatReducer';
import type { ClientChatMessage } from '../../types/chat.types';

interface MessageListProps {
  messages: ClientChatMessage[];
  channelId: string;
  /** The message the "New Messages" divider goes before, if any. */
  dividerBeforeId: string | null;
  /** Where the channel's join is: the empty list says "loading" while it is on its way. */
  join: ChannelJoinStatus;
  hasMore: boolean;
  isLoadingMore: boolean;
  /** The last older page failed: it is retried by hand, not by scrolling. */
  loadMoreFailed: boolean;
  onLoadMore: () => void;
  onRetryMessage: (messageId: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({ 
  messages, 
  channelId,
  dividerBeforeId,
  join,
  hasMore,
  isLoadingMore,
  loadMoreFailed,
  onLoadMore,
  onRetryMessage
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const prevScrollHeight = useRef<number>(0);

  // Ask for the older page, remembering the height to keep the view in place
  const loadOlder = useCallback(() => {
    if (listRef.current) {
      prevScrollHeight.current = listRef.current.scrollHeight;
    }
    onLoadMore();
  }, [onLoadMore]);

  // Intersection Observer for infinite scroll. Not after a failed page: the
  // sentinel would still be in view and ask again at once, forever.
  useEffect(() => {
    if (!hasMore || isLoadingMore || loadMoreFailed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadOlder();
        }
      },
      { 
        root: listRef.current,
        threshold: 0.1 
      }
    );

    if (topSentinelRef.current) {
      observer.observe(topSentinelRef.current);
    }

    return () => {
      if (topSentinelRef.current) {
        observer.unobserve(topSentinelRef.current);
      }
    };
  }, [hasMore, isLoadingMore, loadMoreFailed, loadOlder]);

  // Preserve scroll position after loading more messages
  useEffect(() => {
    if (!isLoadingMore && prevScrollHeight.current > 0 && listRef.current) {
      const newScrollHeight = listRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeight.current;
      
      // Restore scroll position
      listRef.current.scrollTop = scrollDiff;
      prevScrollHeight.current = 0;
    }
  }, [messages.length, isLoadingMore]);

  // Check if user is at bottom of scroll
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    setIsAtBottom(atBottom);
    
    // Clear new messages count if at bottom
    if (atBottom) {
      setNewMessagesCount(0);
    }
  }, []);

  // Auto-scroll to bottom when new messages arrive (only if already at bottom).
  // An older page is not new: it grows the list at the top, leaving the last
  // message where it was.
  const firstMessageId = messages[0]?.id;
  const lastMessageId = messages[messages.length - 1]?.id;
  const previousList = useRef({ length: messages.length, firstMessageId, lastMessageId });
  useEffect(() => {
    const previous = previousList.current;
    previousList.current = { length: messages.length, firstMessageId, lastMessageId };
    const olderPageLoaded = previous.lastMessageId === lastMessageId && previous.firstMessageId !== firstMessageId;
    if (messages.length <= previous.length || olderPageLoaded) return;

    if (isAtBottom && lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    } else if (!isAtBottom && messages.length > 0) {
      // Increment new messages count if not at bottom
      setNewMessagesCount(prev => prev + 1);
    }
  }, [messages.length, firstMessageId, lastMessageId]);

  // Reset new messages count when scrolling to bottom
  useEffect(() => {
    if (isAtBottom) {
      setNewMessagesCount(0);
    }
  }, [isAtBottom]);

  // Scroll to bottom when channel changes
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
      setNewMessagesCount(0);
    }
  }, [channelId]);

  const scrollToBottom = () => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  };

  if (messages.length === 0 && !isLoadingMore) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400">
        {join === 'joining' ? (
          <div className="text-sm" role="status">Loading messages...</div>
        ) : join === 'joined' ? (
          <div className="text-center">
            <div className="text-lg mb-2">No messages yet</div>
            <div className="text-sm">Be the first to say something!</div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="h-full relative">
      <div
        ref={listRef}
        className="h-full overflow-y-auto p-4 space-y-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent"
        onScroll={handleScroll}
      >
        {/* Loading indicator at top, or a retry when the last page failed */}
        {hasMore && (loadMoreFailed ? (
          <div className="h-8 flex items-center justify-center gap-2 text-sm" role="alert">
            <span className="text-gray-300">Older messages could not be loaded.</span>
            <button
              type="button"
              onClick={loadOlder}
              className="font-semibold text-game-golden hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-game-golden rounded"
            >
              Retry
            </button>
          </div>
        ) : (
          <div ref={topSentinelRef} className="h-8 flex items-center justify-center">
            {isLoadingMore && (
              <div className="text-gray-400 text-sm">Loading more messages...</div>
            )}
          </div>
        ))}

        {/* Messages */}
        {messages.map((message, index) => {
          const previousMessage = messages[index - 1];
          const shouldGroupWithPrevious = 
            previousMessage &&
            previousMessage.userId === message.userId &&
            new Date(message.createdAt).getTime() - new Date(previousMessage.createdAt).getTime() < 5 * 60 * 1000; // 5 minutes

          // The first message from someone else since the member last read
          // the channel, placed from the join's answer
          const showNewMessagesDivider = message.id === dividerBeforeId;

          return (
            <div key={message.id}>
              {showNewMessagesDivider && (
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 border-t border-game-accent/30"></div>
                  <span className="text-game-accent text-xs font-medium px-2">New Messages</span>
                  <div className="flex-1 border-t border-game-accent/30"></div>
                </div>
              )}
              <MessageItem
                message={message}
                isGrouped={shouldGroupWithPrevious}
                onRetry={onRetryMessage}
              />
            </div>
          );
        })}
        <div ref={lastMessageRef} />
      </div>

      {/* New messages indicator */}
      {!isAtBottom && newMessagesCount > 0 && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-20 right-4 bg-game-accent text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-2 hover:bg-game-golden transition-colors"
        >
          <span className="text-sm font-medium">
            {newMessagesCount} new {newMessagesCount === 1 ? 'message' : 'messages'}
          </span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </button>
      )}
    </div>
  );
};